import React, { useState, useEffect, useCallback } from 'react';
import { eventService } from '../services';
import { getAvatarUrl } from '../data/mockData';
import { useSEO, jsonLd } from '../utils/seo';
import './EventsPage.css';

const MEETING_ICONS = { zoom: '📹', meet: '🎥', teams: '🟣', custom: '🔗' };
const TYPE_LABELS = { workshop: 'Workshop', webinar: 'Webinar', qa: 'Q&A', office_hours: 'Office Hours', hackathon: 'Hackathon' };

export default function EventsPage() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true); // eslint-disable-line

  useEffect(() => {
    eventService.getEvents()
      .then(res => setEvents(res.events || []))
      .catch(err => { console.error('Events load error:', err); setEvents([]); })
      .finally(() => setLoading(false));
  }, []);


  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');

  useSEO({
    title: 'Mentor Events & Workshops',
    description: 'Join free and paid live workshops, webinars, and office hours with top tech mentors.',
    jsonLd: jsonLd.organization(),
  });

  const filtered = events.filter(e => {
    if (filter !== 'all' && e.status !== filter) return false;
    if (search && !e.title.toLowerCase().includes(search.toLowerCase()) &&
        !e.tags.some(t => t.toLowerCase().includes(search.toLowerCase()))) return false;
    return true;
  });

  // Called by EventCard after a successful registration to bump the local count
  const handleRegistered = useCallback((id) => {
    setEvents(prev => prev.map(e =>
      e.id === id
        ? { ...e, registeredCount: Math.min(e.registeredCount + 1, e.maxAttendees) }
        : e
    ));
  }, []);

  return (
    <div className="events-page">
      <div className="events-hero">
        <div className="events-hero-inner">
          <div className="events-badge">📅 Live Events</div>
          <h1>Learn Live from <span>Top Mentors</span></h1>
          <p>Free and paid workshops, webinars, and office hours. Join live or watch recordings.</p>
          <div className="events-search-row">
            <input
              className="events-search"
              placeholder="🔍 Search events by topic or tag…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="events-body">
        <div className="events-filters">
          {['all', 'upcoming', 'completed'].map(f => (
            <button key={f} className={`ef-btn ${filter === f ? 'on' : ''}`} onClick={() => setFilter(f)}>
              {f === 'all' ? `All (${events.length})` : f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>

        <div className="events-grid">
          {filtered.map(event => (
            <EventCard key={event.id} event={event} onRegistered={handleRegistered} />
          ))}
          {filtered.length === 0 && (
            <div className="events-empty">
              <div style={{ fontSize: '2.5rem' }}>📭</div>
              <p>No events found matching your criteria.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function EventCard({ event: e, onRegistered }) {
  const [registered, setRegistered]   = useState(false);
  const [registering, setRegistering] = useState(false);
  const [showRating, setShowRating]   = useState(false);
  const [myRating, setMyRating]       = useState(0);
  const isFull = e.registeredCount >= e.maxAttendees;

  const handleRegister = async () => {
    if (isFull || registering || registered) return;
    setRegistering(true);
    try {
      await eventService.registerForEvent(e.id);
      setRegistered(true);
      onRegistered?.(e.id);
    } catch (err) {
      console.error('Registration failed:', err);
    } finally {
      setRegistering(false);
    }
  };

  return (
    <div className={`event-card ${e.status === 'completed' ? 'completed' : ''}`}>
      {/* Status ribbon */}
      <div className={`ec-ribbon ${e.status}`}>
        {e.status === 'live' ? '🔴 LIVE NOW' : e.status === 'upcoming' ? '📅 Upcoming' : '✓ Completed'}
      </div>

      <div className="ec-body">
        {/* Header */}
        <div className="ec-header">
          <div className="ec-type-row">
            <span className="ec-type">{TYPE_LABELS[e.type] || e.type}</span>
            {e.priceINR === 0
              ? <span className="ec-free">FREE</span>
              : <span className="ec-price">₹{e.priceINR}</span>
            }
          </div>
          <h3 className="ec-title">{e.title}</h3>
          <p className="ec-desc">{e.description}</p>
        </div>

        {/* Tags */}
        <div className="ec-tags">
          {e.tags.map(t => <span key={t} className="ec-tag">{t}</span>)}
        </div>

        {/* Mentor */}
        <div className="ec-mentor">
          <img
            src={getAvatarUrl(e.mentorAvatar, e.mentorBg, 34)}
            alt={e.mentor}
            className="ec-av"
            onError={ev => { ev.target.style.display = 'none'; ev.target.nextSibling.style.display = 'flex'; }}
          />
          <div className="ec-av-fb" style={{ background: `#${e.mentorBg}` }}>{e.mentor[0]}</div>
          <div>
            <div className="ec-mentor-name">{e.mentor}</div>
            <div className="ec-mentor-role">{e.mentorRole}</div>
          </div>
        </div>

        {/* Meta row */}
        <div className="ec-meta">
          <span>📅 {e.date}</span>
          <span>🕐 {e.time}</span>
          <span>⏱ {e.duration} min</span>
          <span>{MEETING_ICONS[e.meetingPlatform]} {e.meetingPlatform === 'meet' ? 'Google Meet' : e.meetingPlatform.charAt(0).toUpperCase() + e.meetingPlatform.slice(1)}</span>
        </div>

        {/* Attendees */}
        <div className="ec-seats">
          <div className="ec-seats-bar">
            <div className="ec-seats-fill" style={{ width: `${Math.min(100, (e.registeredCount / e.maxAttendees) * 100)}%`, background: isFull ? '#ef4444' : '#1DB954' }} />
          </div>
          <span className="ec-seats-label">
            {isFull ? '🔴 Full' : `${e.registeredCount}/${e.maxAttendees} registered`}
          </span>
        </div>

        {/* Rating (completed) */}
        {e.status === 'completed' && e.rating && (
          <div className="ec-rating-row">
            <span style={{ color: '#f59e0b' }}>{'★'.repeat(Math.floor(e.rating))}{'☆'.repeat(5 - Math.floor(e.rating))}</span>
            <span style={{ fontWeight: 800, fontSize: '.84rem' }}>{e.rating}</span>
            <span style={{ color: '#9ca3af', fontSize: '.72rem' }}>({e.totalRatings} ratings)</span>
          </div>
        )}

        {/* Testimonials (completed) */}
        {e.status === 'completed' && e.testimonials?.length > 0 && (
          <div className="ec-testimonials">
            {e.testimonials.slice(0, 1).map((t, i) => (
              <div key={i} className="ec-testimonial">
                <span className="ect-name">"{t.comment}"</span>
                <span className="ect-author">— {t.name}</span>
              </div>
            ))}
          </div>
        )}

        {/* Recording link */}
        {e.status === 'completed' && e.recordingUrl && (
          <a href={e.recordingUrl} target="_blank" rel="noreferrer" className="ec-recording">
            ▶ Watch Recording
          </a>
        )}

        {/* Actions */}
        <div className="ec-actions">
          {e.status === 'upcoming' && (
            <>
              {registered ? (
                <div className="ec-registered">✅ You're registered! Link sent to email.</div>
              ) : (
                <button
                  className={`ec-register-btn ${isFull ? 'full' : ''}`}
                  onClick={handleRegister}
                  disabled={isFull || registering}
                >
                  {registering
                    ? 'Registering…'
                    : isFull
                      ? '🔴 Event Full'
                      : e.priceINR === 0
                        ? 'Register Free →'
                        : `Register · ₹${e.priceINR} →`}
                </button>
              )}
              {/* Social share */}
              <div className="ec-share">
                {e.socialLinks?.linkedin && (
                  <a href={e.socialLinks.linkedin} target="_blank" rel="noreferrer" className="ec-share-btn linkedin">in</a>
                )}
                {e.socialLinks?.twitter && (
                  <a href={e.socialLinks.twitter} target="_blank" rel="noreferrer" className="ec-share-btn twitter">𝕏</a>
                )}
              </div>
            </>
          )}

          {e.status === 'completed' && !showRating && (
            <button className="ec-rate-btn" onClick={() => setShowRating(true)}>⭐ Rate This Event</button>
          )}
          {e.status === 'completed' && showRating && (
            <div className="ec-rate-form">
              <div style={{ display: 'flex', gap: 6, justifyContent: 'center' }}>
                {[1,2,3,4,5].map(n => (
                  <button key={n} style={{ fontSize: '1.5rem', background: 'none', border: 'none', cursor: 'pointer', color: n <= myRating ? '#f59e0b' : '#d1d5db' }}
                    onClick={() => setMyRating(n)}>★</button>
                ))}
              </div>
              <button className="ec-register-btn" style={{ marginTop: 8 }} onClick={() => setShowRating(false)}>Submit Rating</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
